import React, { useState, useEffect } from 'react';
import styles from './ProductTable.module.css';
import * as XLSX from 'xlsx';
import { productsApi } from '../../../../api/products/productsApi';

// ✅ CHANGE 1: Update component signature to accept props
const ProductTable = ({ onProductClick, savedState }) => {
  // ✅ CHANGE 2: Initialize states with savedState values
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(savedState?.currentPage || 0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState(savedState?.searchTerm || '');
  const [selectedCategory, setSelectedCategory] = useState(savedState?.selectedCategory || '');
  const [selectedStock, setSelectedStock] = useState(savedState?.selectedStock || 'all');
  const [sortBy, setSortBy] = useState(savedState?.sortBy || '');
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder || 'desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 20;

  const stockFilters = [
    { label: 'All', value: 'all' },
    { label: 'In Stock', value: 'in' },
    { label: 'Out of Stock', value: 'out' }
  ];

  // ✅ CHANGE 3: Add function to get current table state
  const getCurrentTableState = () => ({
    currentPage,
    searchTerm,
    selectedCategory,
    selectedStock,
    sortBy,
    sortOrder
  });

  // ✅ CHANGE 4: Add function to handle product click
  const handleProductClick = (itemCode) => {
    if (onProductClick) {
      onProductClick(itemCode, getCurrentTableState());
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await productsApi.getCategories();
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching products...');
      console.log('Page:', currentPage, 'Search:', searchTerm, 'Category:', selectedCategory, 'Stock:', selectedStock);
      
      const data = await productsApi.getProducts(
        currentPage, 
        itemsPerPage, 
        searchTerm, 
        selectedCategory, 
        selectedStock,
        sortBy,
        sortOrder
      );
      
      console.log('✅ Products received:', data);
      
      if (data && data.products && Array.isArray(data.products)) {
        setProducts(data.products);
        setTotalPages(data.totalPages || 0);
        setTotalItems(data.totalItems || 0);
      } else {
        console.error('❌ Invalid data structure:', data);
        setProducts([]);
        setError('Invalid data received from server');
      }
    } catch (err) {
      setError('Failed to load products');
      console.error('❌ Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchProducts();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedStock('all');
    setSortBy('');
    setSortOrder('desc');
    setCurrentPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(0);
  };

  const handleStockChange = (value) => {
    setSelectedStock(value);
    setCurrentPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      console.log('📊 Exporting products to Excel...');
      
      const data = await productsApi.exportProducts(searchTerm, selectedCategory, selectedStock);
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data.map(product => ({
        'CAT No.': product.itemCode,
        'Stock Status': product.stockStatus,
        'Item Name': product.itemName,
        'Item Type': product.itemType,
        'CAS No': product.casNo,
        'IUPAC Name': product.iupacName,
        'Synonyms': product.synonyms,
        'Applications': product.applications,
        'Structure': product.structure,
        'Category': product.category,
        'Stock': product.stock,
        'Created Date': product.createDate,
        'Updated Date': product.updateDate,
        'Total Revenue Generated': product.totalRevenue,
        'Units Sold': product.unitsSold,
        'Number of Customers': product.numCustomers
      })));
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Products');
      XLSX.writeFile(workbook, `all_products_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      console.log('✅ Exported', data.length, 'records to Excel');
    } catch (err) {
      console.error('❌ Error exporting products:', err);
      alert('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.filtersSection}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by CAT No, Item Name, CAS No, IUPAC Name, Synonyms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button onClick={handleClearSearch} className={styles.clearSearchButton} title="Clear search">
                ✕
              </button>
            )}
          </div>
          <button onClick={handleSearch} disabled={loading} className={styles.searchButton}>
            Search
          </button>
        </div>

        <div className={styles.filterRow}>
          <div className={styles.statusButtonGroup}>
            {stockFilters.map((filter) => (
              <button
                key={filter.value}
                className={`${styles.statusButton} ${selectedStock === filter.value ? styles.statusButtonActive : ''}`}
                onClick={() => handleStockChange(filter.value)}
                disabled={loading}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={styles.filterSelect}
              disabled={loading}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.actionButtons}>
            <button onClick={handleClearFilters} disabled={loading} className={styles.clearButton}>
              Clear All Filters
            </button>
            <button onClick={handleExportExcel} disabled={loading || totalItems === 0} className={styles.excelButton}>
              <span className={styles.excelIcon}>📊</span>
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {(searchTerm || selectedCategory || selectedStock !== 'all') && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFiltersLabel}>Active Filters:</span>
          {searchTerm && (
            <div className={styles.filterTag}>
              Search: "{searchTerm}"
              <button onClick={handleClearSearch} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedCategory && (
            <div className={styles.filterTag}>
              Category: {selectedCategory}
              <button onClick={() => handleCategoryChange('')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
          {selectedStock !== 'all' && (
            <div className={styles.filterTag}>
              Stock: {stockFilters.find(f => f.value === selectedStock)?.label}
              <button onClick={() => handleStockChange('all')} className={styles.removeFilterButton}>✕</button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading products...</div>
        </div>
      )}

      {!loading && (
        <div className={styles.tableContainer}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th} onClick={() => handleSort('itemCode')}>
                    CAT No. {getSortIcon('itemCode')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('stockStatus')}>
                    Stock Status {getSortIcon('stockStatus')}
                  </th>
                  <th className={styles.th}>Item Name</th>
                  <th className={styles.th}>Item Type</th>
                  <th className={styles.th}>CAS No</th>
                  <th className={styles.th}>IUPAC Name</th>
                  <th className={styles.th}>Synonyms</th>
                  <th className={styles.th}>Applications</th>
                  <th className={styles.th}>Structure</th>
                  <th className={styles.th}>Category</th>
                  <th className={styles.th} onClick={() => handleSort('stock')}>
                    Stock {getSortIcon('stock')}
                  </th>
                  <th className={styles.th}>Created Date</th>
                  <th className={styles.th}>Updated Date</th>
                  <th className={styles.th} onClick={() => handleSort('totalRevenue')}>
                    Total Revenue {getSortIcon('totalRevenue')}
                  </th>
                  <th className={styles.th} onClick={() => handleSort('unitsSold')}>
                    Units Sold {getSortIcon('unitsSold')}
                  </th>
                  <th className={styles.th}>Customers</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((product, index) => (
                    <tr key={`${product.itemCode}-${index}`} className={styles.tr}>
                      {/* ✅ CHANGE 5: Make CAT No clickable */}
                      <td 
                        className={`${styles.td} ${styles.clickableCell}`}
                        onClick={() => handleProductClick(product.itemCode)}
                        title="Click to view details"
                      >
                        {product.itemCode}
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.stockBadge} ${product.stockStatus === 'In Stock' ? styles.stockInStock : styles.stockOutOfStock}`}>
                          {product.stockStatus}
                        </span>
                      </td>
                      <td className={styles.td} title={product.itemName}>{product.itemName}</td>
                      <td className={styles.td}>{product.itemType || 'N/A'}</td>
                      <td className={styles.td}>{product.casNo || 'N/A'}</td>
                      <td className={styles.td}>{product.iupacName || 'N/A'}</td>
                      <td className={styles.td}>{product.synonyms || 'N/A'}</td>
                      <td className={styles.td}>{product.applications || 'N/A'}</td>
                      <td className={styles.td}>{product.structure || 'N/A'}</td>
                      <td className={styles.td}>{product.category || 'N/A'}</td>
                      <td className={styles.td}>{product.stock?.toFixed(2) || '0.00'}</td>
                      <td className={styles.td}>{formatDate(product.createDate)}</td>
                      <td className={styles.td}>{formatDate(product.updateDate)}</td>
                      <td className={styles.td}>{formatCurrency(product.totalRevenue)}</td>
                      <td className={styles.td}>{product.unitsSold?.toFixed(2) || '0.00'}</td>
                      <td className={styles.td}>{product.numCustomers || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className={styles.td} colSpan="16" style={{ textAlign: 'center', padding: '40px' }}>
                      {searchTerm || selectedCategory || selectedStock !== 'all' ? 'No products match your filters' : 'No products found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && products.length > 0 && (
        <div className={styles.paginationSection}>
          <div className={styles.paginationInfo}>
            Showing {currentPage * itemsPerPage + 1} - {Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems} records
          </div>
          <div className={styles.paginationControls}>
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={styles.paginationButton}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className={styles.paginationButton}
            >
              Prev
            </button>
            <div className={styles.pageNumber}>{currentPage + 1} / {totalPages}</div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className={styles.paginationButton}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={styles.paginationButton}
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;